// GasBuddy Integration Service
// Fetches real-time fuel prices from GasBuddy

import type { StationPrices, FuelPrice } from '@jrd/core';

const GASBUDDY_API_BASE = 'https://www.gasbuddy.com/graphql';

// GasBuddy GraphQL query for station prices
const STATION_PRICES_QUERY = `
  query GetStation($id: ID!) {
    station(id: $id) {
      id
      name
      address {
        line1
        city
        state
        postalCode
      }
      latitude
      longitude
      prices {
        fuelProduct
        credit {
          price
          postedTime
        }
      }
    }
  }
`;

const NEARBY_STATIONS_QUERY = `
  query GetNearbyStations($lat: Float!, $lng: Float!, $radius: Float!) {
    locationByCoordinates(latitude: $lat, longitude: $lng) {
      stations(first: 20, radiusMiles: $radius) {
        edges {
          node {
            id
            name
            address {
              line1
              city
              state
            }
            latitude
            longitude
            distance
            prices {
              fuelProduct
              credit {
                price
                postedTime
              }
            }
          }
        }
      }
    }
  }
`;

// Map GasBuddy fuel products to our grades (no diesel)
function mapFuelProduct(product: string): '87' | '89' | '91' | null {
  const productLower = product.toLowerCase();
  if (productLower.includes('regular') || productLower.includes('unleaded') || product === '87') return '87';
  if (productLower.includes('midgrade') || productLower.includes('plus') || product === '89') return '89';
  if (productLower.includes('premium') || productLower.includes('supreme') || product === '91') return '91';
  return null;
}

export interface GasBuddyStation {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  distance?: number;
  prices: {
    grade: '87' | '89' | '91';
    price: number;
    postedTime: string;
  }[];
}

// Fetch station by GasBuddy ID
export async function fetchStationPrices(stationId: string): Promise<StationPrices | null> {
  try {
    const response = await fetch(GASBUDDY_API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: STATION_PRICES_QUERY,
        variables: { id: stationId },
      }),
    });

    const data = await response.json();
    const station = data?.data?.station;

    if (!station) return null;

    const prices: FuelPrice[] = station.prices
      .map((p: { fuelProduct: string; credit?: { price: number; postedTime: string } }) => {
        const grade = mapFuelProduct(p.fuelProduct);
        if (!grade || !p.credit?.price) return null;
        return {
          grade,
          price: p.credit.price,
          updatedAt: new Date(p.credit.postedTime),
        };
      })
      .filter(Boolean) as FuelPrice[];

    return {
      stationId: station.id,
      name: station.name,
      address: `${station.address.line1}, ${station.address.city}, ${station.address.state}`,
      lat: station.latitude,
      lng: station.longitude,
      prices,
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error('Failed to fetch GasBuddy prices:', error);
    return null;
  }
}

// Fetch nearby competitors
export async function fetchNearbyStations(
  lat: number,
  lng: number,
  radiusMiles = 10
): Promise<GasBuddyStation[]> {
  try {
    const response = await fetch(GASBUDDY_API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: NEARBY_STATIONS_QUERY,
        variables: { lat, lng, radius: radiusMiles },
      }),
    });

    const data = await response.json();
    const edges = data?.data?.locationByCoordinates?.stations?.edges || [];

    return edges.map((edge: { node: Record<string, unknown> }) => {
      const node = edge.node as {
        id: string;
        name: string;
        address: { line1: string; city: string; state: string };
        latitude: number;
        longitude: number;
        distance: number;
        prices: { fuelProduct: string; credit?: { price: number; postedTime: string } }[];
      };
      return {
        id: node.id,
        name: node.name,
        address: `${node.address.line1}, ${node.address.city}`,
        lat: node.latitude,
        lng: node.longitude,
        distance: node.distance,
        prices: node.prices
          .map((p) => {
            const grade = mapFuelProduct(p.fuelProduct);
            if (!grade || !p.credit?.price) return null;
            return {
              grade,
              price: p.credit.price,
              postedTime: p.credit.postedTime,
            };
          })
          .filter(Boolean) as GasBuddyStation['prices'],
      };
    });
  } catch (error) {
    console.error('Failed to fetch nearby stations:', error);
    return [];
  }
}

// Alternative: Use public GasBuddy page scraping for reliability
// This is more reliable than the GraphQL API which may be restricted
export async function fetchStationPricesFromAPI(stationId: string): Promise<StationPrices | null> {
  try {
    // Use the API worker endpoint if configured
    const apiUrl = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_URL || 'https://jrd-fuel-auto-api.jrdcompanies.workers.dev';
    const response = await fetch(`${apiUrl}/api/gasbuddy/station/${stationId}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data as StationPrices;
  } catch (error) {
    console.error('API fetch failed, trying direct:', error);
    return fetchStationPrices(stationId);
  }
}

export async function fetchCompetitorsFromAPI(
  lat: number,
  lng: number,
  radiusMiles = 10
): Promise<GasBuddyStation[]> {
  try {
    const apiUrl = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_URL || 'https://jrd-fuel-auto-api.jrdcompanies.workers.dev';
    const response = await fetch(
      `${apiUrl}/api/gasbuddy/nearby?lat=${lat}&lng=${lng}&radius=${radiusMiles}`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data as GasBuddyStation[];
  } catch (error) {
    console.error('API fetch failed, trying direct:', error);
    return fetchNearbyStations(lat, lng, radiusMiles);
  }
}
